import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Typography,
    Avatar,
    IconButton,
    Paper,
    CircularProgress,
    Tooltip,
} from "@mui/material";
import { Mail, Phone, Users, Check, X, Maximize2 } from "lucide-react";
import { DescriptionDialog } from "../components/common/DescriptionDialog";
import moment from "moment";
import { useNotification } from "../context/NotificationContext";
import {
    useDeleteTodoItemMutation,
    useUpdateTodoItemMutation,
} from "../redux/service/modules/sales";
import { useResponsiveStyles } from "../styles/useResponsiveStyles";

const TaskItem = ({ task }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const { getFontSize, getSectionStyle } = useResponsiveStyles();
    const { showNotification } = useNotification();
    const [updateTodoItem, { isLoading: updateLoading }] =
        useUpdateTodoItemMutation();
    const [deleteTodoItem, { isLoading: deleteLoading }] =
        useDeleteTodoItemMutation();

    const handleOpenDialog = (element) => {
        setAnchorEl(element);
        setDialogOpen(true);
    };
    const handleCloseDialog = () => setDialogOpen(false);

    const getStatusBgColor = (status, days) => {
        if (status === "overdue") {
            return "#FEEEEF";
        } else if (status === "pending" && days < 0) {
            return "#FEEEEF";
        } else if (status === "completed") {
            return "#EEFAF8";
        } else {
            return "#FFF4D6D4";
        }
    };

    const getStatusTextColor = (status, days) => {
        if (status === "overdue") {
            return "#ED1C24";
        } else if (status === "pending" && days < 0) {
            return "#ED1C24";
        } else if (status === "completed") {
            return "#03781D";
        } else {
            return "#4C4E64DE";
        }
    };

    const getStatusText = (status, days) => {
        if (status === "pending") {
            if (days < 0) {
                return `Overdue : By ${Math.abs(days)} Days`;
            } else {
                return `Pending : ${days} Day Left`;
            }
        } else if (status === "completed") {
            return "Completed";
        } else {
            return status;
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-IN", {
            timeZone: "UTC",
            month: "short",
            day: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    };

    const deleteTodo = async (id) => {
        await deleteTodoItem({ id })
            .unwrap()
            .then((response) => {
                showNotification(
                    "success",
                    "",
                    "To-Do Item Deleted successfully!"
                );
            })
            .catch((error) => {
                if (error.status === 400) {
                    showNotification(
                        "error",
                        "An Error Occurred",
                        "Failed to Delete To-Do Item"
                    );
                }
            });
    };

    const updateTodo = async (task) => {
        let form = JSON.parse(JSON.stringify(task));
        form["status"] = "completed";
        await updateTodoItem({ data: form })
            .unwrap()
            .then((response) => {
                if (response) {
                    showNotification(
                        "success",
                        "",
                        "To-Do Item Completed successfully!"
                    );
                } else {
                    showNotification(
                        "error",
                        "An Error Occurred",
                        "Failed to Complete To-Do Item"
                    );
                }
            })
            .catch((error) => {
                if (error.status === 400) {
                    showNotification(
                        "error",
                        "An Error Occurred",
                        "Failed to Completed To-Do Item"
                    );
                }
            });
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                pt: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
                "&:last-child": {
                    borderBottom: "none",
                },
                "&:first-child": {
                    borderTop: "none",
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                }}
            >
                <Box sx={{ display: "flex", gap: 2 }}>
                    <Avatar
                        src={task?.user_image || null}
                        sx={getSectionStyle("avatar")}
                    >
                        {!task?.user_image && task?.created_by_name
                            ? task.created_by_name.charAt(0)
                            : null}
                    </Avatar>
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                color: "#4C4E64DE",
                                ...getFontSize("sub-heading"),
                            }}
                        >
                            {task.title}
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                gap: 1,
                                alignItems: "center",
                                mt: 0.5,
                            }}
                        >
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: "16px",
                                    color: "#979797",
                                    ...getFontSize("sub-heading"),
                                }}
                            >
                                By:{" "}
                                <Typography
                                    component="span"
                                    sx={{
                                        ...getFontSize("sub-heading"),
                                        color: "#40CDFA",
                                        fontWeight: 700,
                                    }}
                                >
                                    {task.created_by_name}
                                </Typography>
                            </Typography>
                            <Box component="span" />
                            <Tooltip title={task?.recipients_name?.join(", ")}>
                                <Typography
                                    component="span"
                                    sx={{
                                        ...getFontSize("sub-heading"),
                                        color: "#979797",
                                        fontWeight: 700,
                                    }}
                                >
                                    Recipient:{" "}
                                    <Typography
                                        component="span"
                                        sx={{
                                            ...getFontSize("sub-heading"),
                                            color: "#03781D",
                                            fontWeight: 600,
                                        }}
                                    >
                                        {task?.recipients_name
                                            ?.slice(0, 3)
                                            .join(", ") +
                                            (task?.recipients_name?.length > 3
                                                ? ", ..."
                                                : "")}
                                    </Typography>
                                </Typography>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography
                        sx={{
                            ...getFontSize("body1"),
                            color: getStatusTextColor(
                                task?.status,
                                moment(new Date(task?.deadline)).diff(
                                    moment(),
                                    "days"
                                )
                            ),
                            bgcolor: `${getStatusBgColor(
                                task?.status,
                                moment(new Date(task?.deadline)).diff(
                                    moment(),
                                    "days"
                                )
                            )}`,
                            py: 0.5,
                            px: 1.5,
                            borderRadius: 5,
                            alignSelf: "center",
                            mr: 1,
                            fontWeight: 600,
                        }}
                    >
                        {getStatusText(
                            task?.status,
                            moment(new Date(task?.deadline)).diff(
                                moment(),
                                "days"
                            )
                        )}
                    </Typography>
                    <Typography
                        sx={{
                            ...getFontSize("body1"),
                            color: "#4C4E64DE",
                            fontWeight: 400,
                        }}
                    >
                        {formatDate(task?.deadline)}
                    </Typography>
                </Box>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Box
                    sx={{
                        maxWidth: "60%",
                        ...getSectionStyle("description-box"),
                    }}
                >
                    <TaskDescription
                        description={task.description}
                        onExpand={handleOpenDialog}
                    />
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                    {task?.type_of_action?.map((action, index) => (
                        <ActionButton
                            key={index}
                            removeRipple={true}
                            icon={
                                action === "email"
                                    ? Mail
                                    : action === "phone"
                                    ? Phone
                                    : action === "in-person"
                                    ? Users
                                    : null
                            }
                            color={
                                action === "email"
                                    ? "#F59D1E"
                                    : action === "phone"
                                    ? "#AF60A8"
                                    : action === "in-person"
                                    ? "#40CDFA"
                                    : "#6b7280"
                            }
                            cursor={"default"}
                        />
                    ))}
                    {task.status !== "completed" && (
                        <>
                            <ActionButton
                                icon={Check}
                                color={"#03781D"}
                                onClick={() => {
                                    updateTodo(task);
                                }}
                                isLoading={updateLoading}
                            />
                            <ActionButton
                                icon={X}
                                color={"#ED1C24"}
                                onClick={() => {
                                    deleteTodo(task.id);
                                }}
                                isLoading={deleteLoading}
                            />
                        </>
                    )}
                </Box>
                <DescriptionDialog
                    open={dialogOpen}
                    onClose={handleCloseDialog}
                    title={""}
                    children={
                        <Typography
                            color="text.secondary"
                            sx={{ ...getFontSize("body1"), lineHeight: 1.6 }}
                        >
                            {task.description}
                        </Typography>
                    }
                    anchorEl={anchorEl}
                />
            </Box>
        </Box>
    );
};

TaskItem.propTypes = {
    task: PropTypes.shape({
        avatar: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        by: PropTypes.string.isRequired,
        recipient: PropTypes.string.isRequired,
        date: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        days: PropTypes.number.isRequired,
        actions: PropTypes.arrayOf(PropTypes.string).isRequired,
    }).isRequired,
};

export default TaskItem;

const ActionButton = ({
    icon: Icon,
    color,
    removeRipple = false,
    onClick,
    isLoading,
    cursor = "pointer",
}) => {
    const { getSectionStyle } = useResponsiveStyles();

    return (
        <IconButton
            sx={{
                ...getSectionStyle("action-button"),
                backgroundColor: `${color}15`,
                color: color,
                "&:hover": {
                    // backgroundColor: `${color}25`,
                    cursor: cursor,
                },
            }}
            onClick={onClick}
            disableRipple={removeRipple}
        >
            {isLoading ? <CircularProgress size={20} /> : <Icon size={22} />}
        </IconButton>
    );
};

export const TaskDescription = ({ description, onExpand }) => {
    const buttonRef = useRef(null);
    const { getFontSize } = useResponsiveStyles();

    const handleExpand = () => {
        if (buttonRef.current) {
            onExpand(buttonRef.current);
        }
    };

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 0.8,
                mb: 2,
                position: "relative",
                backgroundColor: "rgba(0, 0, 0, 0.02)",
                borderColor: "rgba(0, 0, 0, 0.08)",
            }}
        >
            <Typography sx={getFontSize("sub-heading")}>Description</Typography>
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    lineHeight: 1.5,
                    pr: 6,
                }}
            >
                {description.split(" ").slice(0, 100).join(" ")}
            </Typography>
            {description.length > 100 && (
                <IconButton
                    ref={buttonRef}
                    size="small"
                    onClick={handleExpand}
                    sx={{
                        position: "absolute",
                        right: 4,
                        top: 4,
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                        },
                    }}
                >
                    <Maximize2 size={16} />
                </IconButton>
            )}
        </Paper>
    );
};
